import { useCurrentActiveAsset } from '@folio/utils/useCurrentActiveAsset';
import {
  Card,
  CardContent,
  Form,
  FormBuilder,
  Label,
  LoadingCard,
  StepperFooter,
  VEHICLE_SELECTION_RESOLVED_FIELD,
  VehicleInputFormField,
  buildFormValuesFromFields,
  getResolver,
  stepperNextWithFork,
  useAssertedParams,
  useStepperContext,
} from '@korint/folio';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  type Asset,
  type SivVehicle,
  clientWithFork,
  sraControllerGetVehicleDetailsQueryOptions,
  toKeyValueFields,
  useAssetControllerCreateAsset,
  useAssetControllerEditAsset,
} from 'korint-sdk';
import type { Field, VehicleInputTab } from 'korint-sdk/react';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

const energyField: Field = {
  name: 'energy',
  type: 'radio',
  options: [
    { value: 'EL', label: 'Électrique' },
    { value: 'EE', label: 'Hybride rechargeable' },
    { value: 'GO', label: 'Essence/Diesel' },
  ],
  required: true,
  isCustomField: true,
};

// Owned by the shared module; hidden here so they are still validated and sent in the payload.
const identityFields: Array<Field> = ['registrationNumber', 'brand', 'model', 'version'].map(
  // The plate is optional (brandModel supports temporary/foreign/no plate); the rest is required.
  name => ({
    name,
    type: 'text',
    required: name !== 'registrationNumber',
    isCustomField: true,
    hidden: true,
  }),
);

// Populated by the version picker / SRA cross-check; numberOfSeats + transmission stay hidden.
const hiddenHelperFields: Array<Field> = [
  { name: 'numberOfSeats', type: 'number', required: false, isCustomField: true, hidden: true },
  { name: 'transmission', type: 'text', required: false, isCustomField: true, hidden: true },
  // Gate that blocks "Suivant" while an Actuel/Proposé choice is pending (driven by the composite).
  {
    name: VEHICLE_SELECTION_RESOLVED_FIELD,
    type: 'checkbox',
    required: true,
    ignoreInPayload: true,
    hidden: true,
  },
];

const baseExtraFields: Array<Field> = [
  {
    name: 'vehicleFirstRegistrationDate',
    type: 'date',
    required: true,
    isCustomField: true,
  },
  {
    name: 'latestPriceInEuro',
    type: 'number',
    required: true,
    isCustomField: true,
  },
  {
    name: 'vehicleMileage',
    DEPRECATED_placeholder: '10 000 km',
    type: 'number',
    required: true,
    isCustomField: true,
  },
  energyField,
  {
    name: 'vehicleSaleDate',
    type: 'date',
    required: true,
    isCustomField: true,
  },
  {
    name: 'vehicleDeliveryDate',
    type: 'date',
    required: true,
    isCustomField: true,
  },
  {
    name: 'contractStartDate',
    type: 'date',
    required: true,
    isCustomField: true,
    min: dayjs().subtract(15, 'day').format('YYYY-MM-DD'),
  },
];

// SIV/SRA tabs expose the exact specs (auto-filled, editable).
const exactSpecFields: Array<Field> = [
  {
    name: 'fiscalPower',
    type: 'number',
    required: true,
    isCustomField: true,
  },
  {
    name: 'unladenWeight',
    type: 'number',
    required: true,
    isCustomField: true,
  },
];

// Manual entry has no precise specs, so the broker sets coarse classes that the submit maps
// back to unladenWeight / fiscalPower / transmission values (the pricing engine reads those).
const manualClassFields: Array<Field> = [
  {
    name: 'weightClass',
    type: 'checkbox',
    required: false,
    ignoreInPayload: true,
  },
  {
    name: 'powerClass',
    type: 'checkbox',
    required: false,
    ignoreInPayload: true,
  },
  {
    name: 'transmissionClass',
    type: 'checkbox',
    required: false,
    ignoreInPayload: true,
  },
];

const vinField = (existingAsset?: Asset): Field => ({
  name: 'vinNumber',
  type: 'text',
  // vinNumber was not required for already-created assets, so keep it required only for new ones.
  required: !existingAsset,
  isCustomField: true,
});

const allFields = (existingAsset?: Asset): Array<Field> => [
  ...identityFields,
  ...hiddenHelperFields,
  vinField(existingAsset),
  ...baseExtraFields,
  ...exactSpecFields,
  ...manualClassFields,
];

function getFieldsForTab(tab: VehicleInputTab, existingAsset?: Asset): Array<Field> {
  const specFields = tab === 'manual' ? manualClassFields : exactSpecFields;
  const withVin = tab === 'plate' ? [] : [vinField(existingAsset)];
  return [...identityFields, ...hiddenHelperFields, ...withVin, ...baseExtraFields, ...specFields];
}

function VehicleAssetForm({
  existingAsset,
  defaultTab,
}: {
  existingAsset?: Asset;
  defaultTab: VehicleInputTab;
}) {
  const { policyId, branchId } = useAssertedParams();
  const stepper = useStepperContext();

  const [activeTab, setActiveTab] = useState<VehicleInputTab>(defaultTab);
  const activeTabRef = useRef<VehicleInputTab>(defaultTab);
  activeTabRef.current = activeTab;

  const unladenWeight = _.get(existingAsset, 'customFields.unladenWeight') as number | undefined;
  const transmission = _.get(existingAsset, 'customFields.transmission') as string | undefined;
  const fiscalPower = _.get(existingAsset, 'customFields.fiscalPower') as number | undefined;

  // biome-ignore lint/suspicious/noExplicitAny: @debt the whole view was defined without field typing
  const form = useForm<any>({
    values: {
      ...buildFormValuesFromFields(allFields(existingAsset), existingAsset),
      contractStartDate: existingAsset && dayjs(existingAsset.startedAt).format('YYYY-MM-DD'),
      weightClass: Boolean(unladenWeight && unladenWeight > 2300 && unladenWeight < 3500),
      powerClass: Boolean(fiscalPower && fiscalPower > 14),
      transmissionClass: Boolean(transmission?.includes('4')),
    },
    resolver: (values, context) =>
      getResolver(getFieldsForTab(activeTabRef.current, existingAsset))(values, context),
  });

  const values = form.watch();

  const { data: sraDetails } = useQuery({
    ...sraControllerGetVehicleDetailsQueryOptions({
      brand: values.brand,
      model: values.model,
      fiscalPower: Number(values.fiscalPower),
      version: values.version,
      numberOfSeats: Number(values.numberOfSeats),
    }),
    enabled:
      activeTab !== 'manual' &&
      !!values.brand &&
      !!values.model &&
      !!values.fiscalPower &&
      !!values.version &&
      !!values.numberOfSeats,
  });

  // In edit mode, don't clobber the broker's saved price/energy with the SRA response on load;
  // only refresh once the broker actually selects a different version.
  const initialVersion = useRef(
    (existingAsset?.customFields as Record<string, unknown> | undefined)?.version,
  );

  useEffect(() => {
    if (!sraDetails) return;
    if (existingAsset && form.getValues('version') === initialVersion.current) return;
    form.setValue('latestPriceInEuro', sraDetails.data[0]?.latestPriceInEuro ?? 0);
    form.setValue('transmission', sraDetails.data[0]?.transmission ?? '');
    form.setValue('energy', sraDetails.data[0]?.energy ?? '');
  }, [sraDetails, existingAsset, form]);

  const handleSivVehicle = (vehicle: SivVehicle) => {
    form.setValue('version', undefined);
    if (vehicle.fiscalPower != null) form.setValue('fiscalPower', vehicle.fiscalPower.toString());
    if (vehicle.energy) form.setValue('energy', vehicle.energy);
    if (vehicle.unladenWeight != null) form.setValue('unladenWeight', vehicle.unladenWeight);
    if (vehicle.firstRegistrationDate) {
      form.setValue('vehicleFirstRegistrationDate', vehicle.firstRegistrationDate.toString());
    }
  };

  const { mutateAsync: createAsset, isPending: isPendingCreateAsset } =
    useAssetControllerCreateAsset({
      mutation: { onSuccess: stepperNextWithFork({ stepper }) },
      client: { client: clientWithFork },
    });

  const { mutateAsync: updateAsset, isPending: isPendingUpdateAsset } = useAssetControllerEditAsset(
    {
      mutation: { onSuccess: stepperNextWithFork({ stepper }) },
      client: { client: clientWithFork },
    },
  );

  const isManual = activeTab === 'manual';
  const mainExtraFields = isManual
    ? [...identityFields, ...hiddenHelperFields, vinField(existingAsset), ...baseExtraFields]
    : getFieldsForTab(activeTab, existingAsset);

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="pt-6">
          <Form {...form}>
            <div className="flex flex-col gap-6">
              <VehicleInputFormField
                field={{
                  type: 'vehicleInput',
                  name: 'vehicleInput',
                  required: false,
                  tabs: ['plate', 'brandModel', 'manual'],
                  identityFields: ['registrationNumber', 'brand', 'model', 'version'],
                }}
                defaultTab={defaultTab}
                onActiveTabChange={setActiveTab}
                onVehicleFound={handleSivVehicle}
              />

              <div className="flex flex-col gap-4 border-t pt-5">
                <FormBuilder form={form} fields={mainExtraFields} />

                {isManual && (
                  <div className="rounded-lg border bg-muted/30 p-4">
                    <Label className="mb-3 block font-medium">Type de véhicule</Label>
                    <FormBuilder form={form} fields={manualClassFields} />
                  </div>
                )}
              </div>
            </div>
          </Form>
        </CardContent>
      </Card>
      <StepperFooter
        nextButtonEnabled={true}
        onNext={form.handleSubmit(async submitted => {
          const contractStartDate = dayjs(submitted.contractStartDate).toISOString();
          const isManual = activeTabRef.current === 'manual';

          const {
            contractStartDate: _contractStartDate,
            weightClass,
            powerClass,
            transmissionClass,
            [VEHICLE_SELECTION_RESOLVED_FIELD]: _vehicleSelectionResolved,
            ...rest
          } = submitted;

          const derivedSpecs = isManual
            ? {
                unladenWeight: weightClass ? 3000 : 1000,
                fiscalPower: powerClass ? 15 : 7,
                transmission: transmissionClass ? '4x4' : 'OTHER',
              }
            : {};

          const customFields = toKeyValueFields({
            ...rest,
            ...derivedSpecs,
            vehicleMileage: Number.parseInt(submitted.vehicleMileage),
            latestPriceInEuro: Number.parseInt(submitted.latestPriceInEuro),
          });

          if (existingAsset) {
            await updateAsset({
              assetId: existingAsset.id,
              data: {
                name: submitted.registrationNumber,
                customFields,
                startedAt: contractStartDate,
              },
              params: { branchId },
            });
          } else {
            await createAsset({
              data: {
                policyId,
                name: submitted.registrationNumber,
                customFields,
                startedAt: contractStartDate,
              },
              params: { branchId },
            });
          }
        })}
        loading={isPendingCreateAsset || isPendingUpdateAsset}
      />
    </div>
  );
}

export function PolicyFunnelAsset() {
  const { policyId, branchId } = useAssertedParams();

  const { data: existingAsset, isLoading: isCurrentAssetLoading } = useCurrentActiveAsset({
    policyId,
    branchId,
  });

  const brand = _.get(existingAsset, 'customFields.brand') as string | undefined;
  const model = _.get(existingAsset, 'customFields.model') as string | undefined;
  const fiscalPower = _.get(existingAsset, 'customFields.fiscalPower') as number | undefined;
  const version = _.get(existingAsset, 'customFields.version') as string | undefined;
  const numberOfSeats = _.get(existingAsset, 'customFields.numberOfSeats') as number | undefined;

  const { data: hasSraData, isLoading: isSraLoading } = useQuery({
    ...sraControllerGetVehicleDetailsQueryOptions({
      brand: brand!,
      model: model!,
      fiscalPower: fiscalPower!,
      version: version!,
      numberOfSeats: numberOfSeats!,
    }),
    enabled:
      !!brand && !!model && fiscalPower !== undefined && !!version && numberOfSeats !== undefined,
    select: data => data.data.length > 0,
  });

  if (isCurrentAssetLoading || isSraLoading) {
    return <LoadingCard />;
  }

  const registrationNumber = existingAsset?.customFields.registrationNumber as string | undefined;
  const hasTemporaryRegistrationNumber = registrationNumber?.startsWith('WW');

  let defaultTab: VehicleInputTab = 'plate';
  if (hasTemporaryRegistrationNumber) {
    defaultTab = 'brandModel';
  }
  if (Boolean(registrationNumber) && !hasTemporaryRegistrationNumber && !hasSraData) {
    defaultTab = 'manual';
  }

  return <VehicleAssetForm existingAsset={existingAsset} defaultTab={defaultTab} />;
}
